// Generated from EBHubAssessmentFinished.json contract

import { z } from 'zod'

import type { EnergyPrice, SubsidyInput } from './custom-assessment-input.types'

// Status types
export type AssessmentStatus = 'ok' | 'failure'

// Calculation base types
export type CalculationBase = 'demandBased' | 'consumptionBased'

// Energy label types
export type EnergyLabel = 'A+' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H'

// Retrofit types
export const RetrofitNameSchema = z.enum([
  'airWaterHeatPump',
  'airAirHeatPump',
  'groundSourceHeatPump',
  'pelletHeating',
  'districtHeating',
  'decentralVentilation',
  'roof',
  'topFloorCeiling',
  'exteriorWallETICS',
  'blowInInsulation',
  'basementExteriorWall',
  'windows',
  'door',
  'roofWindows',
  'basementCeiling',
  'floorPlate',
])

export type RetrofitName = z.infer<typeof RetrofitNameSchema>

// Range interface
export interface Range {
  min: number
  max: number
}

// U-Values interface
export interface UValues {
  before: Range
  after: Range
}

// Retrofit details interface
export interface RetrofitDetails {
  powerInKw?: number
  uValues?: UValues
  componentAreaInSqm?: number
  insulationThicknessInMm?: number
}

// Annual energy interface
export interface AnnualEnergyInKwh {
  total: number
}

// Energetics interface
export interface Energetics {
  annualFinalEnergyInKwh: AnnualEnergyInKwh
  annualPrimaryEnergyInKwh?: AnnualEnergyInKwh
  annualCo2EmissionsInTons?: number
  transmissionHeatLossInWPerK?: number
  heatingLoadInKw: number
  energyLabel: EnergyLabel
}

// Costs interface
export interface RetrofitCostsInCt {
  low: number
  mean: number
  high: number
}

// Retrofit state economics interface
export interface RetrofitStateEconomics {
  retrofitCostsInCt: RetrofitCostsInCt
  subsidyRateInPct: number
  amortizationTimeInYears: number
  realEstateValuationIncreaseInPct: number
  avgEnergyCostSavingsInCtWithinFiveYears: number
  avgEnergyCostSavingsInPctWithinFiveYears: number
}

// Current state interface
export interface CurrentState {
  energetics: Energetics
}

// Retrofit state interface
export interface RetrofitState {
  retrofitName: RetrofitName
  details: RetrofitDetails
  energetics: Energetics
  economics: RetrofitStateEconomics
}

// Assessment interface (shared by both demand-based and consumption-based)
export interface Assessment {
  calculationBase: CalculationBase
  currentState: CurrentState
  retrofitStates: RetrofitState[]
}

// Assessment input interface
export interface AssessmentInput {
  energyPrices: EnergyPrice[]
  subsidyInput: SubsidyInput
}

// Main EBHubAssessmentFinished event interface
export interface EBHubAssessment {
  buildingId: string
  status: AssessmentStatus
  demandBasedAssessment: Assessment
  consumptionBasedAssessment: Assessment
  assessmentInput: AssessmentInput
}

// Assessment processing status types (for Firestore)
export type AssessmentProcessingStatus = 'processed' | 'processing' | 'failure'

// Firestore assessment document interface
export interface FirestoreAssessmentDocument {
  status: AssessmentProcessingStatus
  mainPackageId?: string
  data: EBHubAssessment
  hiddenRetrofits?: RetrofitName[]
}
